/**
 * The key rename, one read deep -- the whole cost of the game changing its
 * name.
 *
 * It shipped as "Lily's World" and filed everything it remembered under
 * `lilysworld.*`; it is Cat World now and files under `catworld.*`.  Every
 * cat, saved look, visited place and mute toggle already in the wild sits
 * on the OLD key, so the new key is not a fresh shelf: the first read
 * ADOPTS the legacy value and writes it forward.  identity.js's paragraph
 * about the token key -- "every cat on the public street is held by a token
 * already sitting in some player's localStorage" -- is exactly what this
 * module exists to keep true across the rename.
 *
 * COPY FORWARD, NEVER DELETE, deliberately.  The legacy key is left
 * precisely where it is, which buys three things: the adoption is
 * idempotent, a player who rolls back to an older build still finds their
 * cat, and no storage failure halfway through this function can lose data
 * -- there is no window in which the value exists under neither key.  The
 * cost is one stale key per shelf, which is bytes, and it is the cheap side
 * of the trade by a wide margin.
 *
 * Storage is hostile everywhere and every failure here is SILENT, the
 * posture every caller already takes (identity.js documents it at length:
 * absent under node, throwing on property access in sandboxed iframes,
 * throwing on write in private mode or on quota).  A read that cannot write
 * the value forward still RETURNS it -- a value read is worth more than a
 * value filed -- and a shelf that throws reads as "nothing there", which is
 * what every call site already handles.
 */

/**
 * Read `key`, adopting `legacy`'s value the first time.
 *
 * @param {Storage | null | undefined} s
 * @param {string} key     the catworld.* key this build writes
 * @param {string} legacy  the lilysworld.* key it replaced
 * @returns {string | null}
 */
export function getAdopting(s, key, legacy) {
  if (!s) return null;
  try {
    const current = s.getItem(key);
    if (current !== null) return current;
    const old = s.getItem(legacy);
    if (old === null) return null;
    try {
      s.setItem(key, old);
    } catch {
      // Full, or read-only: the value is still the answer to this read.
    }
    return old;
  } catch {
    return null;
  }
}
